// FOLLOW_UP_REPLY — ICT-1 / ICT-8 "Responses": a solicitor or issuer answers
// one of our material follow-ups. Ace reads what the reply says
// (utils/followUpReply.ts), checks MIC, and drafts the SOP's next step:
//   material sent     → MIC shows it? log only. Tracking given? ask the
//                       Fulfillment Center to locate it. Else ask for tracking.
//   dates changing    → forward to Discovery (documents@).
//   Canadian opt-out  → forward to Discovery and ask them to abort the job.
//   PDFs attached     → forward to documents@ + verifications@.
//   handling this year (last year's solicitor, ICT-1 Issuer follow-ups)
//                     → ask Discovery to update the Broker Search Card party.
//   not handling      → continued research: Contact Tracing via the manager.
//   shortage not needed (ICT-13) → tell documents@ + verifications@, close it.
//   asks for POD (ICT-13) → within 100 pages and 50 shorted, ask
//                       documents@ + verifications@ to POD it at no cost;
//                       outside that, it's the manager's call.
//   unclear           → nothing drafted; escalate if unsure.
// The person can change Ace's read before approving; every outcome is
// documented in MIC.
import type { ProxyJob } from '../mockProxyJobs';
import type { ProxyInboxEmail } from '../mockProxyEmails';
import { SOLICITOR_CONTACT_LIST } from '../proxySolicitorContacts';
import { classifyEmail } from '../../utils/proxyEmailClassifier';
import { readReply, readTrackingNumber, REPLY_KIND_LABEL, type ReplyKind } from '../../utils/followUpReply';
import { evaluatePod, isSkippedMaterial } from '../demoShortages';
import type {
  ProxyTask, ProxyWorkflowContext, ProxyWorkflowDefinition,
  ProxyExtractedField, ProxyDraftSchema, ProxyReviewPanelSchema, ProxyPanelContext,
} from '../proxyWorkflowTypes';

const DISCOVERY = 'documents@betanxt.com';
const VERIFICATIONS = 'verifications@betanxt.com';
const FULFILLMENT_CENTER = 'proxymail@betanxt.com';
const PROXY_MATERIAL = 'proxymaterial@betanxt.com';
const ICT_MANAGER = 'ict-manager@betanxt.com';

function replyText(email: ProxyInboxEmail): string {
  return `${email.subject} ${email.body}`;
}

// Done once this particular reply has been logged in MIC.
function replyResolved(job: ProxyJob, email: ProxyInboxEmail): boolean {
  return job.followUpHistory.some(r => r.type === 'reply_actioned' && r.sourceEmailId === email.id);
}

function buildTasks({ jobs, emails }: ProxyWorkflowContext): ProxyTask[] {
  const tasks: ProxyTask[] = [];
  for (const email of emails) {
    if (!email.relatedJobId || classifyEmail(email).category !== 'FOLLOW_UP_REPLY') continue;
    const job = jobs.find(j => j.jobId === email.relatedJobId);
    if (!job || replyResolved(job, email)) continue;
    const kind = readReply(replyText(email));
    tasks.push({
      id: `reply-${email.id}`,
      workflowType: 'FOLLOW_UP_REPLY',
      jobId: job.jobId,
      title: `${job.issuer}: solicitor reply`,
      subtitle: `Job ${job.jobId}`,
      reason: `${email.from} replied to our follow-up: ${REPLY_KIND_LABEL[kind].toLowerCase()}.`,
      source: 'email',
      sourceEmailId: email.id,
      createdAt: email.date,
      // Rule-based read — confident when a rule matched, low when none did.
      confidence: email.aceConfidence ?? (kind === 'unclear' ? 60 : 90),
    });
  }
  return tasks;
}

// The read lives in the panel (it needs the email); edits only hold the
// person's correction.
function defaultEdits(): Record<string, any> {
  return {};
}

interface Plan {
  action: string;
  draft?: Omit<ProxyDraftSchema, 'key' | 'icon'>;
  steps: string[];
  flag?: string;
  teamUpdate?: ProxyReviewPanelSchema['teamUpdate'];
  awaitMaterial?: boolean;
  closesTicket?: boolean;
}

function planFor(kind: ReplyKind, job: ProxyJob, email: ProxyInboxEmail): Plan {
  const ref = `Job #: ${job.jobId}\nCUSIP: ${job.cusip}\nIssuer: ${job.issuer}`;
  const quoted = `${email.from} wrote:\n"${email.body}"`;
  switch (kind) {
    case 'material_sent': {
      if (job.materialStatus === 'Received') {
        return { action: 'MIC shows it received, so just log the reply', steps: [] };
      }
      const tracking = readTrackingNumber(email.body);
      // ICT-13: the short sets are coming to Fulfillment, who merge them in.
      if (job.materialStatus === 'Shortage') {
        return {
          action: 'Tell Fulfillment the material is on the way',
          steps: ['Tell Fulfillment it\'s on the way, so they can merge it when it arrives'],
          teamUpdate: {
            kind: 'material-on-the-way', title: `On the way: ${job.issuer}`,
            body: `${email.from} has sent the short sets for Job ${job.jobId}${tracking ? ` (tracking ${tracking})` : ''}. Merge them into the original receipt when they arrive.`,
          },
          awaitMaterial: true,
        };
      }
      if (tracking) {
        return {
          action: 'Ask the Fulfillment Center to locate it',
          draft: {
            label: 'Locate material → Fulfillment Center', to: FULFILLMENT_CENTER, cc: [PROXY_MATERIAL],
            subject: `Locate proxy material - ${job.issuer} (Job ${job.jobId})`,
            body: `Hello,\n\nThe solicitor says this material was sent (tracking ${tracking}), but MIC doesn't show it checked in. Are you able to locate it?\n\n${ref}\n\nThank you,\nICT / Collections`,
          },
          steps: ['Email the Fulfillment Center with the tracking number'],
        };
      }
      return {
        action: 'Ask the solicitor for tracking',
        draft: {
          label: `Ask for tracking → ${email.from}`, to: email.fromEmail, cc: [PROXY_MATERIAL],
          subject: `Re: ${email.subject.replace(/^re:\s*/i, '')}`,
          body: `Hello,\n\nThank you. MIC doesn't show the material checked in yet. Could you send the tracking number so our fulfillment center can locate it?\n\n${ref}\n\nThank you,\nICT / Collections`,
        },
        steps: ['Reply asking for the tracking number'],
      };
    }
    case 'dates_changing':
      return {
        action: 'Forward to Discovery',
        draft: {
          label: 'Date change → Discovery', to: DISCOVERY,
          subject: `Dates changing - ${job.issuer} (Job ${job.jobId})`,
          body: `Hello,\n\nForwarding a reply to our material follow-up. Please update the job dates.\n\n${ref}\n\n${quoted}\n\nThank you,\nICT / Collections`,
        },
        steps: ['Forward the reply to Discovery (documents@)'],
      };
    case 'opted_out':
      return {
        action: 'Ask Discovery to abort the job',
        draft: {
          label: 'Abort request → Discovery', to: DISCOVERY,
          subject: `Please abort - ${job.issuer} (Job ${job.jobId})`,
          body: `Hello,\n\nThe issuer is a Canadian issuer and has opted out of distributing to US intermediaries. Please abort this job.\n\n${ref}\n\n${quoted}\n\nThank you,\nICT / Collections`,
        },
        steps: ['Forward the reply to Discovery asking them to abort the job'],
        closesTicket: true,
      };
    case 'pdf_attached':
      return {
        action: 'Forward the PDFs',
        draft: {
          label: 'PDFs → Discovery + Verifications', to: DISCOVERY, cc: [VERIFICATIONS],
          subject: `Material PDFs - ${job.issuer} (Job ${job.jobId})`,
          body: `Hello,\n\nPDFs received in reply to our follow-up, attached.\n\n${ref}\n\nThank you,\nICT / Collections`,
        },
        steps: ['Forward the PDFs to documents@ and verifications@'],
      };
    case 'handling_this_year':
      return {
        action: 'Ask Discovery to update the Broker Search Card',
        draft: {
          label: 'Update BSC party → Discovery', to: DISCOVERY,
          subject: `Update Broker Search Card party - ${job.issuer} (Job ${job.jobId})`,
          body: `Hello,\n\n${job.priorYearSolicitor || email.from} confirmed they are handling this year's job. Please update the Broker Search Card party in BOC.\n\n${ref}\n\nThank you,\nICT / Collections`,
        },
        steps: ['Ask Discovery to update the Broker Search Card party in BOC', `Follow up with ${job.priorYearSolicitor || email.from} from now on`],
      };
    case 'not_handling':
      return {
        action: 'Keep researching with Contact Tracing',
        draft: {
          label: 'Contact tracing request → ICT Manager', to: ICT_MANAGER,
          subject: `Contact tracing needed - ${job.issuer} (Job ${job.jobId})`,
          body: `${email.from} says they aren't handling this year's job. Per ICT-1, more research is needed. Please run Contact Tracing so outreach can go out.\n\n${ref}`,
        },
        steps: ['Ask the ICT manager for Contact Tracing'],
      };
    case 'material_not_needed':
      return {
        action: 'Tell Discovery and Verifications, and close the shortage',
        draft: {
          label: 'Shortage not needed → Discovery + Verifications', to: DISCOVERY, cc: [VERIFICATIONS],
          subject: `Shorted material not needed - ${job.issuer} (Job ${job.jobId})`,
          body: `Hello,\n\nThe solicitor says the shorted material isn't needed for this mailing, so there's nothing more to chase.\n\n${ref}\n\n${quoted}\n\nThank you,\nICT / Collections`,
        },
        steps: ['Let documents@ and verifications@ know', 'Tell Fulfillment nothing more is coming', 'Close the shortage'],
        teamUpdate: {
          kind: 'shortage-update', title: `Shortage closed: ${job.issuer}`,
          body: `The solicitor says the short sets on Job ${job.jobId} aren't needed, so nothing more is coming. The shortage is closed.`,
        },
        closesTicket: true,
      };
    case 'pod_requested': {
      const short = podCheck(job);
      return {
        action: short.eligible ? 'Print the shorted sets on demand, at no cost' : 'Manager approves the POD',
        draft: {
          label: 'POD request → Discovery + Verifications', to: DISCOVERY, cc: [VERIFICATIONS],
          subject: `POD the shorted material - ${job.issuer} (Job ${job.jobId})`,
          body: `Hello,\n\nThe solicitor has asked us to print the shorted material on demand. ${short.eligible ? 'It\'s within the no-cost POD guideline, so please go ahead.' : 'It\'s outside the no-cost POD guideline, and the ICT manager has approved it.'}\n\n${ref}\n\n${quoted}\n\nThank you,\nICT / Collections`,
        },
        steps: ['Ask documents@ and verifications@ to POD the shorted sets', 'Tell Fulfillment the printed sets are coming'],
        flag: short.eligible ? undefined : short.reason,
        teamUpdate: {
          kind: 'material-on-the-way', title: `Being printed: ${job.issuer}`,
          body: `The short sets on Job ${job.jobId} are being printed on demand. Merge them into the original receipt when they're ready.`,
        },
        awaitMaterial: true,
      };
    }
    default:
      return {
        action: 'Log the reply and let a person decide the next step',
        steps: [],
        flag: 'Ace couldn\'t tell what this reply needs. Pick what it says above, or use Escalate if you\'re unsure how to proceed.',
      };
  }
}

// ICT-13 §7.i: the internal POD guideline, over the lines still being chased.
function podCheck(job: ProxyJob) {
  const lines = (job.shortageLines || []).filter(l => !isSkippedMaterial(l.materialType));
  const failing = lines.map(l => evaluatePod({ pages: l.pages, shortage: l.shortage })).find(v => !v.eligible);
  return failing ?? { eligible: true, reason: '' };
}

const KIND_OPTIONS = (Object.keys(REPLY_KIND_LABEL) as ReplyKind[]).map(k => ({ value: k, label: REPLY_KIND_LABEL[k] }));

function buildPanel({ job, edits, email }: ProxyPanelContext): ProxyReviewPanelSchema {
  // Always email-sourced by construction — ProxyTaskPanel resolves it.
  const src = email!;
  const kind: ReplyKind = edits.kind ?? readReply(replyText(src));
  const plan = planFor(kind, job, src);
  const tracking = readTrackingNumber(src.body);
  const micBody = `Reply from ${src.from}: ${REPLY_KIND_LABEL[kind]}. ${plan.action}.`;

  const extractedFields: ProxyExtractedField[] = [
    { key: 'issuer', label: 'Issuer', display: job.issuer },
    { key: 'jobId', label: 'Job #', display: job.jobId },
    { key: 'from', label: 'From', display: src.from },
    { key: 'kind', label: 'Reply says (Ace read)', display: REPLY_KIND_LABEL[kind], editable: { kind: 'enum', value: kind, options: KIND_OPTIONS } },
    { key: 'materialStatus', label: 'MIC material status', display: job.materialStatus },
    ...(tracking ? [{ key: 'tracking', label: 'Tracking #', display: tracking }] : []),
  ];

  return {
    extractedFields,
    calculation: {
      title: 'Response handling',
      ruleText: 'ICT-1 / ICT-8: document every response in MIC and action it.',
      rows: [
        { label: 'Reply', value: REPLY_KIND_LABEL[kind] },
        ...(kind === 'material_sent' ? [{ label: 'MIC check-in', value: job.materialStatus === 'Received' ? 'Received' : 'Not checked in' }] : []),
        ...(plan.flag ? [{ label: 'Manager', value: plan.flag, tone: 'amber' as const }] : []),
      ],
      totalRow: { label: 'Recommended action', value: plan.action, tone: plan.draft ? 'positive' : 'muted' },
    },
    drafts: plan.draft ? [{ key: 'reply', icon: '📧', ...plan.draft }] : [],
    approveSummary: [
      ...plan.steps,
      `Log the reply in MIC: "${REPLY_KIND_LABEL[kind]}"`,
      'Move this task to Done.',
    ],
    approveConfirmation: plan.draft
      ? `${plan.action}. Sent to <strong>${plan.draft.to}</strong> and documented for Job ${job.jobId}.`
      : `Reply documented in MIC for Job ${job.jobId}.`,
    teamUpdate: plan.teamUpdate,
    awaitMaterial: plan.awaitMaterial,
    closesTicket: plan.closesTicket,
    escalation: kind === 'unclear' ? 'Ace couldn\'t tell what this reply needs, and ICT-1 says to ask the manager when unsure.'
      : kind === 'pod_requested' && !podCheck(job).eligible ? `${podCheck(job).reason} (ICT-13).` : undefined,
    outboundActions: [
      ...(plan.draft ? [{ key: 'reply', followUpType: `reply_${kind}`, recipientEmail: plan.draft.to, ccEmails: plan.draft.cc, subject: plan.draft.subject || '', bodyPreview: plan.draft.body }] : []),
      { key: 'mic', followUpType: 'reply_actioned', recipientEmail: 'mic@betanxt.com', subject: `Follow-up reply documented for Job ${job.jobId}`, bodyPreview: micBody },
    ],
  };
}

// Once Discovery is asked to update the BSC, last year's solicitor is the
// job's solicitor, so later chases go to them with the normal template.
function onApprove({ job, edits, email }: ProxyPanelContext): Partial<ProxyJob> | void {
  const kind: ReplyKind = edits.kind ?? readReply(replyText(email!));
  if (kind === 'handling_this_year' && job.priorYearSolicitor) {
    return { solicitor: job.priorYearSolicitor, solicitorEmail: SOLICITOR_CONTACT_LIST[job.priorYearSolicitor] ?? null };
  }
  // Discovery has the job now; chasing waits until they've updated it.
  if (kind === 'dates_changing') return { heldForDiscovery: 'Meeting dates changing' };
  if (kind === 'opted_out') return { heldForDiscovery: 'Abort requested' };
  // Nothing more to chase on the shortage.
  if (kind === 'material_not_needed') return { materialStatus: 'Received' };
}

export const FOLLOW_UP_REPLY_WORKFLOW: ProxyWorkflowDefinition = {
  type: 'FOLLOW_UP_REPLY',
  label: 'Solicitor Reply',
  noun: 'a solicitor reply',
  trigger: 'A solicitor or issuer replies to one of our material follow-ups (email classified FOLLOW_UP_REPLY).',
  source: 'Inbound email (classified by utils/proxyEmailClassifier.ts, read by utils/followUpReply.ts).',
  steps: ['Source & extraction', 'Response handling', 'Draft & approve'],
  buildTasks,
  defaultEdits,
  buildPanel,
  onApprove,
};
